import React, { useState, useMemo, useRef } from 'react';
import { useDismissOnOutsideClick } from '../../hooks/useDismissOnOutsideClick';
import { Panel, Group as PanelGroup, Separator as PanelResizeHandle, useGroupRef } from 'react-resizable-panels';
import { useResourceViewerStore, getDetachedWindow } from '../../stores/resourceViewerStore';
import {
  useResource,
  useResourcePdf,
  useResourceText,
  useResourceTranslation,
  useResourceRedactions,
  useSaveRedactions,
  useReprocess,
  useAddResourceTag,
  useRedactionEvents,
} from '../../hooks/useResource';
import { useProject } from '../../hooks/useProject';
import { useAuditAction, auditUri } from '../../hooks/useAuditLog';
import { useDocumentHotkeys } from '../../hooks/useDocumentHotkeys';
import { hasPermission } from '../../types/project';
import type { ViewMode, ResourceBates, ChatMessage, EntityHighlight } from '../../types/resource';
import type { GridApi } from 'ag-grid-community';
import { saveSelectedMessages, saveEntireConversation } from '../../hooks/useConversationMessages';
import { notifyError, notifySuccess } from '../../services/notify';
import { showApiErrorToast } from '../../services/errorToast';
import PdfViewer from './PdfViewer';
import PlainViewer from './PlainViewer';
import LargeFileWarning from './LargeFileWarning';
import MediaPlayer from './MediaPlayer';
import ConversationView from './ConversationView';
import ImportLogButton from './ImportLogButton';
import ExceptionsViewer from './ExceptionsViewer';
import TranslationPanel from './TranslationPanel';
import ReviewPanel from './ReviewPanel';
import DetachedViewer, { openDetachedWindow } from './DetachedViewer';
import { HotkeysModal } from './HotkeysModal';
import { PendingChangesModal } from './PendingChangesModal';
import { ReplacePdfModal } from './ReplacePdfModal';
import { getConfigService, getTokenService, isSuperAdmin } from '../../services/angular-bridge';

// <audio>/<video> can't send an Authorization header, so /playable authenticates via the
// query string (as Angular's resource_text.playerSrc did); without it the server returns 403.
function playerSrc(projectId: string, collectionId: string, resourceId: string): string {
  const config = getConfigService();
  const query = new URLSearchParams({
    client_id: config.getClientId(),
    access_token: getTokenService().GetAccessToken(),
  });
  return `${config.getServerBaseUrl()}/project/${projectId}/collection/${collectionId}/resource/${resourceId}/playable?${query}`;
}

// Identifies an export across documents; imported endorsements have no exportId.
function batesKey(b: ResourceBates): string {
  return b.exportId ?? b.importId ?? b.name;
}

// The download button saves the rendered PDF, so name it from the PDF source URL (a
// pre-signed S3 path ending in the .pdf key) rather than the native filename. Using URL()
// drops the query string and percent-decodes the segment. Returns undefined if unparseable
// so PdfViewer's 'document.pdf' default applies.
function pdfFilenameFromUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  try {
    const { pathname } = new URL(url);
    const last = decodeURIComponent(pathname.slice(pathname.lastIndexOf('/') + 1));
    return last || undefined;
  } catch {
    return undefined;
  }
}

// Text at or above this size is fetched only on request.
const MAX_TEXT_BYTES = 1_000_000;
const MAX_PDF_BYTES = 10_000_000;

function resourceIcon(resource: { hasVirus?: boolean; type?: string } | null | undefined): string {
  if (!resource) return 'fa-file';
  if (resource.hasVirus) return 'fa-bug';
  switch (resource.type) {
    case 'container': return 'fa-archive';
    case 'email': return 'fa-envelope';
    case 'extracted conversation': return 'fa-comments';
    default: return 'fa-file';
  }
}

export default function ResourceViewer() {
  const { isOpen, config, close: closeViewer, browseIndex, totalRows, next: nextDoc, prev: prevDoc, detach, popIn, detached, gridAdapter, viewInConversation, enterConversation, pendingChanges, disregardChanges } = useResourceViewerStore();
  // Next/Previous/Close warn first when a redaction or note is unsaved (mirrors Angular's
  // gridNextRow/gridPreviousRow/close). The deferred action runs on "Disregard Changes".
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const guarded = React.useCallback((action: () => void) => () => {
    const { redaction, notes } = useResourceViewerStore.getState().pendingChanges;
    if (redaction || notes) setPendingAction(() => action);
    else action();
  }, []);
  const close = useMemo(() => guarded(closeViewer), [guarded, closeViewer]);
  const closeRequestCount = useResourceViewerStore(s => s.closeRequestCount);
  React.useEffect(() => {
    if (closeRequestCount) close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeRequestCount]);
  const next = useMemo(() => guarded(() => void nextDoc()), [guarded, nextDoc]);
  const prev = useMemo(() => guarded(() => void prevDoc()), [guarded, prevDoc]);
  // Open the window synchronously in the click so the popup blocker allows it; only
  // then flip store state. If blocked (win null), stay inline.
  const handleDetach = () => {
    const win = openDetachedWindow();
    if (win) detach(win);
  };
  // When detached, the viewer body (incl. its modals) lives in the pop-out window.
  // Its document is the extra target for hotkeys and the portal target for modals so
  // shortcuts + dialogs work whichever window has focus.
  const detachedWindow = detached ? getDetachedWindow() : null;
  const detachedDoc = detachedWindow?.document;
  // Lock the host page's scroll while the viewer is inline, so the document list behind
  // it doesn't scroll (mirrors Angular's `body.resource-layer-active { overflow: hidden }`).
  // When detached, the viewer lives in its own window, so the host page scrolls normally.
  const lockHostScroll = isOpen && !detached;
  React.useEffect(() => {
    if (!lockHostScroll) return;
    document.body.classList.add('resource-layer-active');
    return () => document.body.classList.remove('resource-layer-active');
  }, [lockHostScroll]);
  // Prev/next only work when a grid registered an adapter (browse/search/collection).
  // Cross-link opens (related/conversation) have no adapter → pager hidden.
  const canPage = !!gridAdapter && totalRows > 0;
  const [viewMode, setViewMode] = useState<ViewMode>('Pdf');
  const [translationVisible, setTranslationVisible] = useState(false);
  // The chosen export, kept across prev/next and resolved against each document's bates.
  const [selectedExport, setSelectedExport] = useState<ResourceBates | null>(null);
  const [searchRequest, setSearchRequest] = useState<{ snippet: string; seq: number } | null>(null);
  // Analytics → Plain Text entity highlighting. Categories highlight every entity of that
  // type at once; picking a single entity replaces any previous single pick.
  const [highlightCategories, setHighlightCategories] = useState<string[]>([]);
  const [highlightEntity, setHighlightEntity] = useState<EntityHighlight | null>(null);
  const [showHotkeys, setShowHotkeys] = useState(false);
  const [showReplacePdf, setShowReplacePdf] = useState(false);
  // Conversation Save dropdown state (footer): selected messages + the messages GridApi
  // (for "Entire Conversation", which extracts from the first displayed row's resource).
  const [selectedMessages, setSelectedMessages] = useState<ChatMessage[]>([]);
  const messagesApiRef = React.useRef<GridApi | null>(null);
  const [savingConversation, setSavingConversation] = useState(false);
  const [saveMenuOpen, setSaveMenuOpen] = useState(false);
  const saveMenuRef = useRef<HTMLDivElement>(null);
  useDismissOnOutsideClick(saveMenuRef, saveMenuOpen, () => setSaveMenuOpen(false));
  // Stable identities so ConversationGrid's api/selection effects don't re-run (and null the
  // ref) on every parent re-render.
  const handleMessagesGridApi = React.useCallback((api: GridApi | null, teardown = false) => {
    // A keyed remount (conversation switch) tears the old grid down in the passive flush,
    // AFTER the new grid's onGridReady — so only honour a teardown from the grid we hold.
    if (teardown) {
      if (messagesApiRef.current === api) messagesApiRef.current = null;
      return;
    }
    messagesApiRef.current = api;
  }, []);

  const { projectId = '', collectionId = '', resourceId = '' } = config ?? {};

  const { data: resource, isLoading, error } = useResource(projectId, collectionId, resourceId);
  const selectedBates =
    (selectedExport && resource?.resourceBates?.find((b) => batesKey(b) === batesKey(selectedExport))) || null;
  // PDFs over 10MB wait for the user to opt in, same as Angular's advanced viewer.
  const isLargePdf = (resource?.pdfBytes ?? 0) > MAX_PDF_BYTES;
  const [showLargePdf, setShowLargePdf] = useState(false);
  const { data: pdfData } = useResourcePdf(
    projectId,
    collectionId,
    resourceId,
    viewMode === 'Pdf' ? resource?.directPdfPath : undefined,
    !!resource && (!isLargePdf || showLargePdf)
  );
  const { data: endorsedPdfData } = useResourcePdf(
    projectId,
    collectionId,
    resourceId,
    viewMode === 'EndorsedPdf' && selectedBates ? selectedBates.directEndorsedPdfPath : undefined,
    viewMode === 'EndorsedPdf' && !!selectedBates
  );
  // Annotated HTML when the resource has one, otherwise the raw text file. Wait for the
  // resource so the direct paths are known — firing early caches the endpoint fallback
  // under the same key.
  const plainIsHtml = !!resource?.directAnnotatedHtmlPath;
  const plainPath = resource?.directAnnotatedHtmlPath ?? resource?.directTextPath;
  // Documents over 1MB of text aren't fetched until the user asks — rendering them
  // inline locks up the viewer.
  const isLargeText = (resource?.textBytes ?? 0) >= MAX_TEXT_BYTES;
  const [showLargeText, setShowLargeText] = useState(false);
  const { data: plainText } = useResourceText(
    projectId,
    collectionId,
    resourceId,
    false,
    plainPath,
    !!resource && (!isLargeText || showLargeText)
  );
  const { data: translation } = useResourceTranslation(projectId, collectionId, resourceId);
  const { data: redactions = [] } = useResourceRedactions(projectId, collectionId, resourceId);
  const saveRedactions = useSaveRedactions(projectId, collectionId, resourceId);
  const { data: project } = useProject(projectId);
  useRedactionEvents(projectId, collectionId, resourceId);
  const reprocess = useReprocess(projectId, collectionId, resourceId);
  const addTag = useAddResourceTag(projectId, collectionId, resourceId);

  // Quick-tag hotkeys need MANAGE_TAGS (same permission that gates the tagging UI).
  // Mirrors Angular's TagService.TagResource: a pure apply of the named tag with
  // tagRelated=false (never a toggle).
  const canTag = !!project && hasPermission(project, 'MANAGE_TAGS');
  const quickTag = React.useCallback(
    (name: string) => { if (canTag) addTag.mutate({ name, tagRelated: false }); },
    [canTag, addTag]
  );

  // Footer action gating (mirrors resource.html): reprocess is super-admin-only; native
  // download is shown for MANAGE_DOWNLOADS on a non-virus resource (Angular's iio-show-if +
  // ng-if="!resource.hasVirus"); reprocess/replace/download are hidden for phone-image
  // metadata (conversation feature).
  const superAdmin = isSuperAdmin();
  const isPhoneImage = resource?.type === 'phone image metadata';
  // Chat resources that offer the "View in Conversation" entry (resource.html ~800).
  const isChatType =
    !!resource && ['instant message', 'sms', 'mms', 'extracted conversation'].includes(resource.type);
  // Phone-image metadata is always in the conversation view (no toggle); the four chat types
  // toggle in via the footer button (Angular: phone-image || viewInConversation).
  const inConversation = viewInConversation || isPhoneImage;

  async function runConversationSave(action: () => Promise<unknown>) {
    setSaveMenuOpen(false);
    setSavingConversation(true);
    try {
      await action();
      notifySuccess({ title: 'Extracting Conversation Started', timeout: 5000 });
    } catch (e) {
      showApiErrorToast(e);
    } finally {
      setSavingConversation(false);
    }
  }

  const handleSaveSelected = () => {
    if (selectedMessages.length === 0) {
      notifyError('Please select the messages you would like to extract.');
      setSaveMenuOpen(false);
      return;
    }
    void runConversationSave(() => saveSelectedMessages(projectId, collectionId, selectedMessages));
  };

  const handleSaveEntire = () => {
    // Angular extracts the entire conversation from the first displayed message's resource.
    // serverSide row 0 is a stub until its block loads — say so rather than no-op silently.
    const first = messagesApiRef.current?.getDisplayedRowAtIndex(0)?.data as ChatMessage | undefined;
    if (!first) {
      notifyError('Conversation is still loading. Try again in a moment.');
      setSaveMenuOpen(false);
      return;
    }
    void runConversationSave(() => saveEntireConversation(projectId, collectionId, first.resourceId));
  };
  // Visibility must NOT depend on directNativePath: trial projects have no native path, and
  // we still want the button shown-but-disabled there as a demo/marketing cue. The path only
  // gates clickability.
  // Print and download hand over the redacted file when the server has burned one; the
  // viewer still renders the original with redaction overlays drawn on top.
  const pdfOutputUrl = resource?.directRedactedPdfPath;
  const audit = useAuditAction(projectId);
  const pdfAuditDetails = () => ({
    resourceId,
    uri: auditUri(pdfOutputUrl ?? resource?.directPdfPath),
  });
  const canManageDownloads = !!project && hasPermission(project, 'MANAGE_DOWNLOADS');

  const canDownloadNative = !resource?.hasVirus && canManageDownloads;
  // Enabled only on active projects with a native path; otherwise disabled with a tooltip
  // (matches Angular ng-disabled="project.status === Status.Trial" + tooltipText).
  const downloadDisabled = project?.status !== 'active' || !resource?.directNativePath;
  const downloadTooltip = project?.status === 'active' ? 'Download' : 'Active Projects Only';

  const isPlayable = resource
    ? resource.mimeType.includes('audio') || resource.mimeType.includes('video')
    : false;

  const availableModes = useMemo<ViewMode[]>(() => {
    if (!resource) return [];
    if (resource.isContainer) return resource.hadProcessingException ? ['Pdf', 'Exceptions'] : [];
    const modes: ViewMode[] = ['Plain', 'Pdf'];
    // Stay on Endorsed PDF while paging through documents outside the export (shows a notice).
    if (resource.resourceBates?.length > 0 || (viewMode === 'EndorsedPdf' && selectedExport)) modes.push('EndorsedPdf');
    if (resource.hadProcessingException) modes.push('Exceptions');
    return modes;
  }, [resource, viewMode, selectedExport]);

  // View mode and export carry over prev/next; a fresh open starts on the PDF.
  React.useEffect(() => {
    if (isOpen) return;
    setViewMode('Pdf');
    setSelectedExport(null);
  }, [isOpen]);

  React.useEffect(() => {
    setTranslationVisible(false);
    setShowLargeText(false);
    setShowLargePdf(false);
  }, [resourceId]);

  // Highlights are scoped to the view that drew them.
  React.useEffect(() => {
    setHighlightCategories([]);
    setHighlightEntity(null);
  }, [viewMode, resourceId]);

  const toggleHighlightCategory = React.useCallback((category: string) => {
    setHighlightEntity(null);
    setHighlightCategories((prev) =>
      prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]
    );
  }, []);

  const selectHighlightEntity = React.useCallback((entity: EntityHighlight) => {
    setHighlightEntity((prev) =>
      prev && prev.type === entity.type && prev.value === entity.value ? null : entity
    );
  }, []);

  const hotkeys = useMemo(
    () => {
      const list = [
        { combo: 'alt+q', handler: close },
        { combo: 'left', handler: () => { if (canPage && browseIndex > 0) prev(); } },
        { combo: 'right', handler: () => { if (canPage && browseIndex < totalRows - 1) next(); } },
        { combo: 'shift+?', handler: () => setShowHotkeys(true) },
      ];
      if (canTag) {
        list.push(
          { combo: 'ctrl+space', handler: () => quickTag('Reviewed') },
          // macOS reserves ctrl+space for switching input sources.
          { combo: 'alt+v', handler: () => quickTag('Reviewed') },
          { combo: 'alt+r', handler: () => quickTag('Responsive') },
          { combo: 'alt+n', handler: () => quickTag('Not Responsive') },
          { combo: 'alt+k', handler: () => quickTag('Confidential') },
          { combo: 'alt+p', handler: () => quickTag('Privileged') },
          { combo: 'alt+j', handler: () => quickTag('Junk') },
        );
      }
      return list;
    },
    [close, canPage, browseIndex, totalRows, prev, next, canTag, quickTag]
  );
  useDocumentHotkeys(hotkeys, isOpen, detachedDoc ?? null);

  const panelGroupRef = useGroupRef();
  const [panelLayout, setPanelLayout] = useState([65, 35]);

  // snippet keeps its <em> markers — PdfViewer highlights the marked term, not the
  // arbitrary window it anchors on.
  function handleSearchTermSelect(snippet: string) {
    if (activeViewMode !== 'Pdf' && activeViewMode !== 'EndorsedPdf') {
      setViewMode('Pdf');
    }
    setSearchRequest((prev) => ({ snippet, seq: (prev?.seq ?? 0) + 1 }));
  }

  function handleResizeHandleDoubleClick() {
    const [left] = panelLayout;
    const target = Math.abs(left - 50) < 3 ? { left: 66, right: 34 } : { left: 50, right: 50 };
    panelGroupRef.current?.setLayout(target);
  }

  if (!isOpen || !config) return null;

  const activeViewMode = availableModes.includes(viewMode) ? viewMode : 'Pdf';

  function renderContent() {
    if (isLoading) {
      return (
        <div className="text-center" style={{ paddingTop: 80 }}>
          <i className="fas fa-spinner fa-spin fa-3x text-muted" />
          <p className="text-muted" style={{ marginTop: 12 }}>Loading document&hellip;</p>
        </div>
      );
    }
    if (error || !resource) {
      return <div className="alert alert-danger">Failed to load document.</div>;
    }
    if (resource.isContainer && activeViewMode !== 'Exceptions') {
      return (
        <div className="alert alert-info alert-block" style={{ marginTop: 45 }}>
          <h4>No preview available</h4>
          <p>This document is a container file and is not available for preview</p>
        </div>
      );
    }
    if (resource.hasVirus) {
      return <div className="alert alert-danger">Virus detected — preview disabled.</div>;
    }
    if (isPlayable) {
      return (
        <MediaPlayer
          src={playerSrc(projectId, collectionId, resourceId)}
          mimeType={resource.mimeType}
        />
      );
    }
    switch (activeViewMode) {
      case 'Pdf':
        if (isLargePdf && !showLargePdf) {
          return (
            <LargeFileWarning
              label="PDF"
              bytes={resource.pdfBytes ?? 0}
              downloadUrl={pdfOutputUrl ?? resource.directPdfPath}
              canDownload={canManageDownloads}
              onShow={() => setShowLargePdf(true)}
              onDownloaded={() => audit('Download PDF', pdfAuditDetails())}
            />
          );
        }
        return pdfData ? (
          // Key by resource so navigation remounts the viewer instead of reusing a
          // stale instance that keeps the previous document's rendered pages.
          <PdfViewer
            key={resourceId}
            pdfData={pdfData}
            filename={pdfFilenameFromUrl(resource.directPdfPath)}
            redactions={redactions}
            onRedactionsChange={(r) => saveRedactions.mutate(r)}
            searchRequest={searchRequest}
            targetDocument={detachedDoc}
            sourceUrl={pdfOutputUrl}
            canDownload={canManageDownloads}
            onPrinted={() => audit('Print PDF', pdfAuditDetails())}
            onDownloaded={() => audit('Download PDF', pdfAuditDetails())}
          />
        ) : (
          <div className="text-center" style={{ paddingTop: 80 }}>
            <i className="fas fa-spinner fa-spin fa-3x text-muted" />
          </div>
        );
      case 'EndorsedPdf':
        if (!selectedBates) {
          return (
            <div className="alert alert-info" style={{ marginTop: 45 }} data-testid="viewer-endorsed-missing">
              {selectedExport
                ? `This document has no endorsed PDF for export ${selectedExport.name}.`
                : 'Select an export to view its endorsed PDF.'}
            </div>
          );
        }
        return endorsedPdfData ? (
          <PdfViewer
            key={`endorsed-${resourceId}-${selectedBates?.directEndorsedPdfPath ?? ''}`}
            pdfData={endorsedPdfData}
            filename={pdfFilenameFromUrl(selectedBates?.directEndorsedPdfPath)}
            searchRequest={searchRequest}
            targetDocument={detachedDoc}
            canDownload={canManageDownloads}
            onPrinted={() =>
              audit('Print PDF', { resourceId, uri: auditUri(selectedBates?.directEndorsedPdfPath) })
            }
            onDownloaded={() =>
              audit('Download PDF', { resourceId, uri: auditUri(selectedBates?.directEndorsedPdfPath) })
            }
          />
        ) : (
          <div className="text-center" style={{ paddingTop: 80 }}>
            <i className="fas fa-spinner fa-spin fa-3x text-muted" />
          </div>
        );
      case 'Plain':
        if (isLargeText && !showLargeText) {
          return (
            <LargeFileWarning
              label="text"
              bytes={resource.textBytes ?? 0}
              downloadUrl={plainPath}
              canDownload={canManageDownloads}
              onShow={() => setShowLargeText(true)}
            />
          );
        }
        return plainText !== undefined ? (
          <PlainViewer
            content={plainText}
            isHtml={plainIsHtml}
            highlightCategories={highlightCategories}
            highlightEntity={highlightEntity}
          />
        ) : (
          <div className="text-center" style={{ paddingTop: 80 }}>
            <i className="fas fa-spinner fa-spin fa-3x text-muted" />
          </div>
        );
      case 'Exceptions':
        return (
          <ExceptionsViewer
            exceptions={resource.processingExceptions}
            redactedException={resource.redactedException}
            tiffException={resource.tiffException}
          />
        );
      default:
        return null;
    }
  }

  const body = (
    <div className="resource" style={{ display: 'flex', flexDirection: 'column' }} data-testid="viewer-panel">
      <button
        className="close"
        data-testid="viewer-detach-btn"
        title={detached ? 'Return to inline view' : 'Open in detached window'}
        aria-label={detached ? 'Pop viewer back inline' : 'Detach viewer'}
        onClick={detached ? popIn : handleDetach}
        style={{ position: 'absolute', top: 7, right: 7, zIndex: 1 }}
      >
        <i className={`fas ${detached ? 'fa-compress' : 'fa-expand'} fa-lg`} aria-hidden="true" />
      </button>
      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        {inConversation && resource ? (
          // Conversation mode replaces the whole split-pane viewer (mirrors Angular hiding
          // the iio-splitter), rather than swapping just the left pane.
          <ConversationView
            projectId={projectId}
            collectionId={collectionId}
            resourceId={resourceId}
            resource={resource}
            canTag={canTag}
            onMessagesSelectionChange={setSelectedMessages}
            onMessagesGridApi={handleMessagesGridApi}
          />
        ) : (
        <PanelGroup
          orientation="horizontal"
          style={{ height: '100%' }}
          groupRef={panelGroupRef}
          onLayoutChanged={(layout) => setPanelLayout(Object.values(layout) as [number, number])}
        >
          <Panel id="left" defaultSize={65} minSize={30} style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            {/* Title header scoped to left pane only */}
            <div className={`page-header resource-header-content clearfix${resource?.hasVirus ? ' alert-danger' : ''}`} style={{ marginBottom: 8 }}>
              <h3 data-testid="viewer-title-heading">
                <i className={`fas ${resourceIcon(resource)}`} />
                {' '}
                {resource?.title || resource?.filename || '—'}
              </h3>
            </div>
            <div className="resource-preview" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, position: 'relative' }}>
              {/* View mode tabs */}
              {availableModes.length > 1 && !viewInConversation && (
                <div className="btn-group" style={{ flexShrink: 0, marginBottom: 10 }}>
                  {availableModes.map((mode) => (
                    <label
                      key={mode}
                      data-testid="viewer-mode-tab"
                      // Exceptions tab is warning-yellow (matches Angular btn-warning);
                      // it only appears when the resource has processing exceptions.
                      className={`btn ${mode === 'Exceptions' ? 'btn-warning' : 'btn-default'}${activeViewMode === mode ? ' active' : ''}`}
                      onClick={() => {
                        if (mode === 'EndorsedPdf' && resource?.resourceBates?.length && !selectedBates) {
                          setSelectedExport(resource.resourceBates[0]);
                        }
                        setViewMode(mode);
                      }}
                    >
                      {mode === 'Plain' && <><i className="far fa-file-alt" /> Plain Text</>}
                      {mode === 'Pdf' && <><i className="far fa-file-pdf" /> PDF View</>}
                      {mode === 'EndorsedPdf' && <><i className="far fa-file-pdf" /> Endorsed PDF View</>}
                      {mode === 'Exceptions' && <><i className="fas fa-warning" /> Exceptions</>}
                    </label>
                  ))}
                  <label
                    className={`btn btn-default${translationVisible ? ' active' : ''}`}
                    onClick={() => setTranslationVisible((v) => !v)}
                    title="Translation"
                    data-testid="viewer-translation-toggle"
                  >
                    <i className="fas fa-language fa-lg" /> Translation
                  </label>
                </div>
              )}

              {/* Prev/next document navigation — driven by the grid adapter */}
              {canPage && !viewInConversation && (
                <div className="resource-pager" style={{ position: 'absolute', top: 0, right: 0, height: 40, display: 'flex', alignItems: 'center' }}>
                  <span className="text-muted" style={{ marginRight: 10, fontSize: 13 }}>
                    {browseIndex + 1} of {totalRows}
                  </span>
                  <ul className="pager" style={{ margin: 0, paddingRight: 20 }}>
                    <li className={browseIndex <= 0 ? 'disabled' : ''}>
                      <a onClick={() => { if (browseIndex > 0) prev(); }} data-testid="viewer-prev-btn">&larr; Previous</a>
                    </li>
                    <li className={browseIndex >= totalRows - 1 ? 'disabled' : ''}>
                      <a onClick={() => { if (browseIndex < totalRows - 1) next(); }} data-testid="viewer-next-btn">Next &rarr;</a>
                    </li>
                  </ul>
                </div>
              )}

              {/* Endorsed PDF bates selector */}
              {activeViewMode === 'EndorsedPdf' && resource && (resource.resourceBates?.length > 0 || selectedExport) && (
                <select
                  className="form-control"
                  style={{ display: 'inline-block', width: 'auto', marginLeft: 8 }}
                  data-testid="viewer-bates-select"
                  value={selectedExport ? batesKey(selectedExport) : ''}
                  onChange={(e) => {
                    const bates = resource.resourceBates.find((b) => batesKey(b) === e.target.value);
                    setSelectedExport(bates ?? null);
                  }}
                >
                  {!selectedExport && <option value="">Select export…</option>}
                  {/* The active export stays selected (disabled) on documents outside it. */}
                  {selectedExport && !selectedBates && (
                    <option value={batesKey(selectedExport)} disabled>{selectedExport.name}</option>
                  )}
                  {resource.resourceBates.map((b) => (
                    <option key={batesKey(b)} value={batesKey(b)}>{b.name}</option>
                  ))}
                </select>
              )}

              {/* Content fills remaining space; translation splits it 50/50 */}
              <div style={{ flex: translationVisible ? '0 0 50%' : 1, minHeight: 0, overflow: 'auto' }}>
                {renderContent()}
              </div>

              {translationVisible && (
                <div style={{ flex: '0 0 50%', minHeight: 0, overflow: 'auto', borderTop: '1px solid #ddd' }}>
                  <TranslationPanel
                    translation={translation}
                    projectId={projectId}
                    collectionId={collectionId}
                    resourceId={resourceId}
                    googleTranslateEnabled={project?.googleTranslateEnabled}
                  />
                </div>
              )}
            </div>
          </Panel>
          <PanelResizeHandle
            style={{ width: 4, background: '#ddd', cursor: 'col-resize' }}
            disableDoubleClick
            onDoubleClick={handleResizeHandleDoubleClick}
          />
          <Panel id="right" defaultSize={35} minSize={20}>
            {resource && (
              <ReviewPanel
                resource={resource}
                projectId={projectId}
                collectionId={collectionId}
                resourceId={resourceId}
                onSearchTermSelect={handleSearchTermSelect}
                canHighlightEntities={activeViewMode === 'Plain' && plainIsHtml}
                highlightCategories={highlightCategories}
                highlightEntity={highlightEntity}
                onToggleHighlightCategory={toggleHighlightCategory}
                onSelectHighlightEntity={selectHighlightEntity}
              />
            )}
          </Panel>
        </PanelGroup>
        )}
      </div>

      <div style={{ flexShrink: 0 }}>
        <div className="resource-footer-content">
          <div className="row">
            <div className="col-sm-6" style={{ textAlign: 'left' }}>
              {/* Hotkeys help — works in the detached window too (modal portals into it). */}
              <button type="button" className="btn btn-default" title="Hotkeys" onClick={() => setShowHotkeys(true)} data-testid="viewer-hotkeys-btn">
                <i className="far fa-keyboard" aria-hidden="true" />
              </button>
              {/* Import Log — phone-image/conversation unsupportedElements (resource.html ~770). */}
              {inConversation && resource?.unsupportedElements && resource.unsupportedElements.length > 0 && (
                <ImportLogButton elements={resource.unsupportedElements} />
              )}
              {resource && !inConversation && (
                <>
                  {superAdmin && (
                    <button
                      type="button"
                      className="btn btn-danger"
                      title="Re-Process"
                      style={{ marginLeft: 4 }}
                      data-testid="viewer-reprocess-btn"
                      disabled={reprocess.isPending}
                      onClick={() => reprocess.mutate()}
                    >
                      <i className="fas fa-cog" aria-hidden="true" />
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-default"
                    title="Replace PDF"
                    style={{ marginLeft: 4 }}
                    data-testid="viewer-replace-pdf-btn"
                    onClick={() => setShowReplacePdf(true)}
                  >
                    <i className="far fa-file-pdf" aria-hidden="true" />
                  </button>
                  {canDownloadNative && (
                    // Tooltip lives on the wrapper span, not the anchor: Bootstrap sets
                    // `pointer-events: none` on a.btn.disabled, so a title on the disabled
                    // anchor never fires on hover. The span still receives the hover (matches
                    // Angular, where uib-tooltip sat on the wrapping div).
                    <span title={downloadTooltip} style={{ marginLeft: 4, display: 'inline-block' }}>
                      <a
                        className={`btn btn-default${downloadDisabled ? ' disabled' : ''}`}
                        href={downloadDisabled ? undefined : resource.directNativePath}
                        download
                        aria-disabled={downloadDisabled}
                        data-testid="viewer-download-native-btn"
                        onClick={(e) => {
                          if (downloadDisabled) { e.preventDefault(); return; }
                          audit('Download Native Resource', {
                            resourceId,
                            uri: auditUri(resource.directNativePath),
                          });
                        }}
                      >
                        <i className="fas fa-download" aria-hidden="true" />
                      </a>
                    </span>
                  )}
                </>
              )}
              {/* View in Conversation — chat resources only (resource.html ~800) */}
              {isChatType && !inConversation && (
                <button
                  type="button"
                  className="btn btn-primary"
                  title="View in Conversation"
                  style={{ marginLeft: 4 }}
                  data-testid="viewer-conversation-btn"
                  onClick={enterConversation}
                >
                  <i className="far fa-comments" aria-hidden="true" />
                </button>
              )}
            </div>
            <div className="col-sm-6">
              {/* Save dropdown — conversation mode only (resource.html ~808). Extract the
                  clicked-selected messages, or the entire conversation. */}
              {inConversation && (
                <div ref={saveMenuRef} className={`btn-group dropup${saveMenuOpen ? ' open' : ''}`} style={{ marginRight: 4 }}>
                  <button
                    type="button"
                    className="btn btn-primary dropdown-toggle"
                    disabled={savingConversation}
                    onClick={() => setSaveMenuOpen((o) => !o)}
                    data-testid="viewer-save-btn"
                  >
                    <i className={`fas ${savingConversation ? 'fa-circle-notch fa-spin' : 'fa-save'} fa-fw`} aria-hidden="true" />
                    {' '}Save <span className="caret" />
                  </button>
                  <ul className="dropdown-menu" role="menu" style={{ right: 0, left: 'auto' }} data-testid="viewer-save-menu">
                    <li><a role="menuitem" onClick={handleSaveSelected} data-testid="viewer-save-selected-menu-item">Selected Messages</a></li>
                    <li><a role="menuitem" onClick={handleSaveEntire} data-testid="viewer-save-entire-menu-item">Entire Conversation</a></li>
                  </ul>
                </div>
              )}
              <button className="btn btn-primary" onClick={close} data-testid="viewer-close-btn">Close</button>
            </div>
          </div>
        </div>
      </div>

      {pendingAction && (
        <PendingChangesModal
          pendingChanges={pendingChanges}
          targetDocument={detachedDoc}
          onCancel={() => setPendingAction(null)}
          onDisregard={() => {
            disregardChanges();
            setPendingAction(null);
            pendingAction();
          }}
        />
      )}
      {showHotkeys && <HotkeysModal canTag={canTag} targetDocument={detachedDoc} onClose={() => setShowHotkeys(false)} />}
      {showReplacePdf && resource && (
        <ReplacePdfModal
          projectId={projectId}
          collectionId={collectionId}
          resourceId={resourceId}
          projectName={project?.name}
          targetDocument={detachedDoc}
          onClose={() => setShowReplacePdf(false)}
        />
      )}
    </div>
  );

  // When detached, render the same body into the child window. Portals preserve React
  // context, so the QueryClient and the shared store still reach the detached DOM —
  // both windows read one store, exactly as the Angular child window shared $rootScope.
  // detachedWindow is computed above (reused for hotkeys + modal portal targets).
  if (detached && detachedWindow) {
    return <DetachedViewer win={detachedWindow} onNativeClose={popIn}>{body}</DetachedViewer>;
  }
  return body;
}
